import { GAME_ASSET_SLUG } from "@/config/game";
import { CardEffects } from "@/design-system/PlayerCard/CardEffects";
import {
  CardPlayStylesPlus,
  CardPrice,
  CardSkillMovesWeakFoot,
  FIRST_OWNER_GLYPH,
  PLAYER_CARD_BASE_HEIGHT,
  PLAYER_CARD_BASE_WIDTH,
} from "@/design-system/PlayerCard/PlayerCardView";
import { MetaRatingChip } from "@/design-system/Tags/MetaRatingChip/MetaRatingChip";
import { cn } from "@/lib/utils";
import { t } from "@repo/shared/i18n";
import { CardFont } from "@repo/shared/types";
import { FACE_STAT_KEYS } from "@repo/shared/utils";
import { memo, useMemo } from "react";
import { useSelector } from "react-redux";

import { selectedMobileFrame } from "../../../../designSystemIcons";
import { extractColors } from "../../../../functions/extractCardColors";
import { EvolutionsIcon } from "../../../../icons";
import {
  contentLabelsSelector,
  eventTokensSelector,
  getVersionAssetsSelector,
} from "../../../../redux/loadedContent/loadedContentSlice";
import {
  Tier,
  getRolesPlusOrPlusPlus,
  tierSelector,
} from "../../../../redux/players/playersSlice";
import {
  SquadBuilderPlayerType,
  chemStylesToIcon,
} from "../../../../redux/squadBuilder/squadBuilderUtils";
import { ClearButton } from "../../../atoms/ClearButton/ClearButton";
import { PlayerSidePanel } from "../../../features/SquadBuilder/components/PlayerSidePanel/PlayerSidePanel";
import Chemistry from "../../Chemistry/Chemistry";
import { PlusPlus } from "../../PlusPlus/PlusPlus";
import { Position } from "./Position/Position";

export type SquadBuilderPlayerProps = {
  player: SquadBuilderPlayerType;
  position: string;
  formationPosition?: string;
  metaRating: number;
  metaRatingZeroChem: number;
  chemStyleId?: number;
  chemistry?: {
    currentChemistry: number;
    previousChemistry: number;
    isOutOfPosition: boolean;
  };
  playerRoleId?: number;
  handleRemovePlayer?: () => void;
  onClickPlayerRole?: () => void;
  onClickPlayer?: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleAddPlayer?: () => void;
  handleChangeChemstyle?: () => void;
  handleAddEvoPlayer?: () => void;
  handleAddPlayerFilters?: () => void;
  isOnPitch?: boolean;
  isOnBench?: boolean;
  showPlayerFilters?: boolean;
  tabletOrLargerMode?: boolean;
  desktopMode?: boolean;
  isSelected?: boolean;
  isDragging?: boolean;
  hidePrice?: boolean;
  isRecommendationCardMobile?: boolean;
};

type RemovePlayerButtonProps = {
  handleRemovePlayer: () => void;
};
export const RemovePlayerButton: React.FC<RemovePlayerButtonProps> = ({
  handleRemovePlayer,
}) => (
  <div className="absolute desktop:right-0 mobile:z-30 touch-device:scale-[117%] -top-3 -right-3 desktop:top-4 p-2 desktop:p-0 z-21">
    <ClearButton onClick={handleRemovePlayer} />
  </div>
);

type BelowCardProps = {
  metaRating: number;
  tier?: Tier;
};

// Meta rating and tier under the card
const BelowCard: React.FC<BelowCardProps> = ({ metaRating, tier }) => (
  <div className="absolute bottom-[-8px] inset-x-0 flex items-center justify-center">
    <div className="text-white scale-55 mobile:scale-75 large-desktop:scale-85">
      <MetaRatingChip metaRating={metaRating || undefined} tier={tier} />
    </div>
  </div>
);

type CardImageProps = {
  cardUrl: string;
};

const CardImage: React.FC<CardImageProps> = memo(({ cardUrl }) => {
  return <img loading="lazy" src={cardUrl} alt={cardUrl} />;
});

type BottomAreaProps = {
  bottomText: string;
  cardName: string;
  cardFont?: CardFont;
  position: string;
  attributes: number[];
  isOnPitch?: boolean;
  isOnBench?: boolean;
  tabletOrLargerMode?: boolean;
  assetsBaseUrl: string;
  countryId: number;
  leagueId: number;
  clubId: number;
  isLight: boolean;
};

const BottomArea: React.FC<BottomAreaProps> = memo(
  ({
    bottomText,
    cardName,
    cardFont,
    position,
    attributes,
    isOnPitch,
    isOnBench,
    tabletOrLargerMode,
    assetsBaseUrl,
    countryId,
    leagueId,
    clubId,
    isLight,
  }) => {
    const { playerAttributes } = useSelector(contentLabelsSelector);
    const faceStatKeys =
      position === "GK" ? FACE_STAT_KEYS.gk : FACE_STAT_KEYS.outfield;
    const clubLeagueCountry = (
      <div
        className={cn(
          "w-full mx-auto justify-center gap-x-1 relative large-desktop:-mt-0.5",
          !isOnPitch && !tabletOrLargerMode ? "scale-[85%] -mt-1" : "mt-1",
        )}
      >
        <img
          alt={t("alt.country")}
          className="absolute large-desktop:w-4 w-3 h-[6px] left-[27%] bottom-[-6px] large-desktop:bottom-[-10px] large-desktop:h-2  large-desktop:left-[25%] mt-[2px]"
          src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/countries/${countryId}.png`}
        />
        <img
          alt={t("alt.league")}
          className="absolute w-2 h-2 -translate-x-1/2 left-1/2 large-desktop:w-3 large-desktop:h-3"
          src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/leagues/${
            isLight ? "" : "dark/"
          }${leagueId}.png`}
        />
        <img
          alt={t("alt.club")}
          className="absolute large-desktop:w-3 large-desktop:h-3 h-2 w-2 right-[32%] large-desktop:right-[30%]"
          src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/clubs/${
            isLight ? "" : "dark/"
          }${clubId}.png`}
        />
      </div>
    );

    return (
      <div
        className="absolute flex flex-col top-[66%] mobile:top-[69%] mobile:-translate-x-1/2 mobile:left-1/2 w-full mobile:w-auto"
        style={{
          color: bottomText,
        }}
      >
        <div className="relative">
          <div
            className={cn(
              // Font size first: tailwind-merge drops `leading-*` that
              // precedes a `text-<size>`. The signature keeps the normal
              // name's line box and overflows it, so the blank stats row and
              // the crests stay put.
              cardFont === "jalliya"
                ? "text-xs large-desktop:text-lg translate-y-1"
                : cn(
                    "large-desktop:text-xs",
                    isOnPitch ? "text-tiny" : "text-xtiny",
                  ),
              "w-[calc(120%)] -ml-2 whitespace-nowrap leading-[8px] mobile:leading-[1px] text-center large-desktop:leading-[4px] -mt-[2px]",
              isOnPitch && "mt-0.5 mobile:-mt-0.5",
            )}
            style={{
              fontFamily:
                cardFont === "jalliya" ? "JalliyaSeven" : "CruyffMedium",
            }}
          >
            {cardName ?? t("squadBuilder:unknownCard")}
          </div>
          {(tabletOrLargerMode || (!isOnPitch && !isOnBench)) && (
            <div
              className={cn(
                "flex flex-row gap-x-[2px] mobile:gap-x-[4px] text-xxtiny large-desktop:text-xtiny leading-[8px] large-desktop:leading-[10px] -mt-0.5 mobile:mt-1 text-center justify-center items-center",
                !isOnPitch &&
                  !tabletOrLargerMode &&
                  "scale-[80%] touch-device:scale-[90%] leading-[7px] mb-0.75 -mt-0.25",
                // An autograph card shows no face stats; the row still
                // sizes the bottom area so the crests sit where they do on a
                // normal card.
                cardFont === "jalliya" && "invisible",
              )}
              style={{
                fontFamily: "CruyffCondensedMedium",
              }}
            >
              {attributes?.map((attribute, index) => (
                <div key={index} className="flex flex-col">
                  <div>{playerAttributes[faceStatKeys[index]]?.short}</div>
                  <div
                    className="large-desktop:text-tiny text-xtiny"
                    style={{ fontFamily: "CruyffMedium" }}
                  >
                    {attribute}
                  </div>
                </div>
              ))}
            </div>
          )}
          {clubLeagueCountry}
        </div>
      </div>
    );
  },
);

type PlayerImageProps = {
  playerUrl: string;
  hasDynamicImage: boolean;
  versionId: number;
  cardName: string;
};

const PlayerImage: React.FC<PlayerImageProps> = memo(
  ({ playerUrl, hasDynamicImage, versionId, cardName }) => (
    <img
      alt={cardName ?? t("squadBuilder:unknownCard")}
      loading="lazy"
      className={cn(
        "absolute flex items-center",
        hasDynamicImage && versionId !== 72
          ? // ? versionId === 72
            //   ? "top-[8%] desktop:top-[-4px] right-2 w-16 large-desktop:right-0 desktop:w-24 large-desktop:w-36"
            //   :
            "w-full -top-1 desktop:right-0 desktop:w-28 large-desktop:w-36"
          : "w-3/5 desktop:w-[75px] large-desktop:w-[84px] desktop:top-[15%] large-desktop:top-[16%] top-[20%] right-[8px] mobile:right-[12px] large-desktop:right-3",
      )}
      src={playerUrl + "?v5"}
    />
  ),
);

type PositionOnPitchProps = {
  formationPosition?: string;
  plus: boolean;
  plusPlus: boolean;
  tabletOrLargerMode?: boolean;
};

const PositionOnPitch: React.FC<PositionOnPitchProps> = ({
  formationPosition,
  plus,
  plusPlus,
  tabletOrLargerMode,
}) => {
  const { positions } = useSelector(contentLabelsSelector);
  return (
    // Flush under the meta rating chip at every size
    <div className="absolute bottom-[-13px] mobile:bottom-[-30px] large-desktop:bottom-[-32px] z-[-1] flex items-center justify-center inset-x-0">
      <Position tabletOrLargerMode={tabletOrLargerMode}>
        <div className="flex flex-row items-center gap-x-[2px] mobile:gap-x-1">
          {formationPosition &&
            (positions[formationPosition]?.name ?? formationPosition)}
          <div className={cn("flex flex-row")}>
            <PlusPlus plus={plus} plusPlus={plusPlus} />
          </div>
        </div>
      </Position>
    </div>
  );
};

type TopLeftAreaProps = {
  rating: number;
  position: string;
  chemStyleId: number;
  firstOwner?: boolean;
  topText: string;
};

const TopLeftArea: React.FC<TopLeftAreaProps> = ({
  rating,
  position,
  chemStyleId,
  firstOwner,
  topText,
}) => {
  const { positions } = useSelector(contentLabelsSelector);
  return (
    <div
      className="absolute top-[21%] left-[14%] large-desktop:top-[21%] large-desktop:left-[13%] flex flex-col text-left"
      style={{ color: topText }}
    >
      <div
        className="text-sm desktop:text-lg large-desktop:text-xl large-desktop:-mt-0.5 leading-4 large-desktop:leading-7"
        style={{ fontFamily: "CruyffMedium" }}
      >
        {rating}
      </div>
      <div
        className="text-tiny large-desktop:text-xs -mt-0.5 large-desktop:mt-[-6px]"
        style={{ fontFamily: "CruyffCondensedMedium" }}
      >
        {positions[position]?.name ?? position}
      </div>
      <div className="w-fit flex flex-col items-center large-desktop:ml-[-1px] -ml-[1px] mt-[-6px]">
        <div
          className="text-xs large-desktop:text-sm"
          style={{ fontFamily: "UltimateTeamIcons" }}
        >
          {chemStylesToIcon[chemStyleId]}
        </div>
        {/* EA's first-owner glyph: small, under the chemstyle */}
        {firstOwner && (
          <div
            className={cn(
              "leading-none text-xtiny large-desktop:text-tiny",
              // Tucked under the chemstyle, or in its slot when there is none
              chemStylesToIcon[chemStyleId] ? "-mt-0.5" : "mt-1",
            )}
            style={{ fontFamily: "UltimateTeamIcons" }}
          >
            {FIRST_OWNER_GLYPH}
          </div>
        )}
      </div>
    </div>
  );
};

type ChemistrySectionProps = {
  chemistry: {
    currentChemistry: number;
    previousChemistry: number;
    isOutOfPosition: boolean;
  };
  tabletOrLargerMode?: boolean;
};

const ChemistrySection: React.FC<ChemistrySectionProps> = ({
  chemistry,
  tabletOrLargerMode,
}) => (
  <div className="absolute w-fit -left-0.5 bottom-1 mobile:bottom-2 mobile:left-[2px] large-desktop:left-2 z-10">
    <Chemistry
      chemistry={chemistry.currentChemistry as 0 | 1 | 2 | 3}
      previousChemistry={chemistry.previousChemistry as 0 | 1 | 2 | 3}
      isOutOfPosition={chemistry.isOutOfPosition}
      weight={tabletOrLargerMode ? 0.9 : 0.7}
      backgroundColor="bg-gray-900"
    />
  </div>
);

const LoanPlayerIcon: React.FC<{ numberOfGames: number }> = ({
  numberOfGames,
}) => (
  <div className="absolute right-0.5 bottom-1 desktop:bottom-2 w-3.5 h-3.5 desktop:w-5 desktop:h-5 shrink-0 rounded-full bg-primary-blue z-10">
    <div className="relative w-full h-full">
      <div className="text-tiny desktop:text-xs text-white desktop:font-semibold absolute inset-0 text-center">
        {numberOfGames}
      </div>
    </div>
  </div>
);

type PlayerSidePanelWrapperProps = {
  handleRemovePlayer: () => void;
  handleAddPlayer?: () => void;
  onClickPlayerRole?: () => void;
  handleChangeChemstyle?: () => void;
  handleAddEvoPlayer?: () => void;
  handleAddPlayerFilters?: () => void;
  showPlayerFilters: boolean;
  evolutionPath?: number[];
  isSelected?: boolean;
};

const PlayerSidePanelWrapper: React.FC<PlayerSidePanelWrapperProps> = ({
  handleRemovePlayer,
  handleAddPlayer,
  onClickPlayerRole,
  handleChangeChemstyle,
  handleAddEvoPlayer,
  handleAddPlayerFilters,
  showPlayerFilters,
  evolutionPath,
}) => {
  return (
    <div className="relative w-full h-full group">
      {/* Pressing the card hides the overlays; pressing the menu doesn't */}
      <div className="absolute inset-0 peer" />
      <div className="absolute desktop:right-0 mobile:z-30 touch-device:scale-[117%] -top-3 -right-3 desktop:top-4 p-2 mobile:p-0 z-21 opacity-0 pointer-events-none transition-opacity duration-300 group-hover:opacity-100 group-hover:pointer-events-auto peer-active:opacity-0">
        <ClearButton onClick={handleRemovePlayer} />
      </div>
      {/* Bench cards only get Role, read-only squads nothing */}
      {(handleAddPlayer || onClickPlayerRole) && (
        <div className="absolute z-30 -right-[105px] top-12 large-desktop:top-16 pointer-events-none opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-hover:pointer-events-auto peer-active:opacity-0">
          <PlayerSidePanel
            onClickInsert={handleAddPlayer}
            onClickRole={onClickPlayerRole}
            onClickChemstyle={handleChangeChemstyle}
            onClickEvolve={handleAddEvoPlayer}
            onClickFilters={
              showPlayerFilters ? handleAddPlayerFilters : undefined
            }
            cantChangeChemstyle={evolutionPath?.length > 0}
          />
        </div>
      )}
    </div>
  );
};

const EvolutionsIconComponent: React.FC<{
  outlineColor: string;
  backgroundColor: string;
  isOnPitch: boolean;
  isOnBench: boolean;
}> = ({ outlineColor, backgroundColor, isOnPitch, isOnBench }) => (
  <div
    className={cn(
      "absolute flex flex-col w-3.5 touch-device:w-4.5 h-3.5 touch-device:h-4.5",
      !isOnPitch && !isOnBench
        ? "bottom-1 touch-device:bottom-2.5 right-1.5 touch-device:right-2.5"
        : "bottom-3 right-2",
    )}
  >
    <EvolutionsIcon
      outlineColor={outlineColor}
      backgroundColor={backgroundColor}
    />
  </div>
);

export const SquadBuilderPlayer = ({
  player: {
    clubId,
    leagueId,
    countryId,
    rating,
    owned,
    cardName,
    cardFont,
    foil,
    cosmetics,
    untradeable,
    firstOwner,
    versionId,
    metalId,
    attributes,
    playStylesPlus,
    hasDynamicImage,
    playerUrl,
    priceInfo,
    objectiveDisplay,
    evoPathId,
    isEvoPlayer,
    evolutionPath,
    isLoanPlayer,
    numberOfLoanedGames,
    rolesPlus,
    skillMoves,
    weakFoot,
  },
  playerRoleId,
  metaRating,
  metaRatingZeroChem,
  chemStyleId,
  position,
  formationPosition,
  chemistry,
  handleRemovePlayer,
  onClickPlayerRole,
  onClickPlayer,
  handleAddPlayer,
  handleChangeChemstyle,
  handleAddEvoPlayer,
  handleAddPlayerFilters,
  isOnPitch,
  isOnBench,
  showPlayerFilters,
  tabletOrLargerMode,
  isSelected,
  desktopMode,
  isDragging,
  hidePrice,
}: SquadBuilderPlayerProps) => {
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const versionAssets = useSelector(getVersionAssetsSelector);
  const eventTokens = useSelector(eventTokensSelector);

  const { topText, bottomText, cardUrl, isLight, strip } = useMemo(() => {
    return extractColors({
      versionAssets,
      versionId,
      metalId,
    });
  }, [versionAssets, versionId, metalId]);

  const isRolesPlusOrPlusPlus = getRolesPlusOrPlusPlus({
    playerRoleId,
    rolesPlus,
  });

  const plus = isRolesPlusOrPlusPlus === "plus";
  const plusPlus = isRolesPlusOrPlusPlus === "plusPlus";

  const tier = useSelector(
    tierSelector(metaRatingZeroChem, playerRoleId, true),
  );

  const showPriceSection =
    !hidePrice && (priceInfo !== undefined || owned || untradeable);

  return (
    <div
      className={cn(
        "relative w-20 h-[110px] mobile:h-[154px] mobile:w-28 large-desktop:w-32 large-desktop:h-[176px] text-[#412b18]",
        isOnPitch || isOnBench ? "cursor-grab" : "cursor-pointer",
      )}
    >
      {/* Above the in-flow card art, under every positioned sibling below. */}
      <CardEffects foil={foil} cosmetics={cosmetics} cardUrl={cardUrl} />
      {isSelected && !desktopMode && (
        <div className="absolute">
          {selectedMobileFrame}
          <RemovePlayerButton handleRemovePlayer={handleRemovePlayer} />
        </div>
      )}
      <div
        className="absolute w-20 h-[110px] mobile:h-[140px] mobile:w-28 large-desktop:w-32 large-desktop:h-[162px] z-20"
        onClick={(e) => {
          onClickPlayer && onClickPlayer(e);
        }}
      >
        {(isOnPitch || isOnBench) && tabletOrLargerMode && !isDragging && (
          <PlayerSidePanelWrapper
            {...{
              handleRemovePlayer,
              handleAddPlayer,
              onClickPlayerRole,
              handleChangeChemstyle,
              handleAddEvoPlayer,
              handleAddPlayerFilters,
              showPlayerFilters,
              evolutionPath,
              isSelected,
            }}
          />
        )}
      </div>
      <CardImage cardUrl={cardUrl} />
      <PlayerImage {...{ playerUrl, hasDynamicImage, versionId, cardName }} />
      {/* Front page card's 180px box, scaled to this card's 80/112/128px width */}
      {/* z-1: PlayStyle+ paints over the first-owner and chemstyle glyphs */}
      <div
        className="absolute top-0 left-0 z-1 origin-top-left scale-[44.44%] mobile:scale-[62.22%] large-desktop:scale-[71.11%]"
        style={{
          width: PLAYER_CARD_BASE_WIDTH,
          height: PLAYER_CARD_BASE_HEIGHT,
        }}
      >
        {showPriceSection && (
          <CardPrice
            {...{
              priceInfo,
              objectiveDisplay,
              eventTokens,
              owned,
              untradeable,
            }}
          />
        )}
        {playStylesPlus?.length > 0 && (
          <CardPlayStylesPlus
            {...{ playStylesPlus, bottomText, strip }}
            belowChemStyle={chemStyleId in chemStylesToIcon}
          />
        )}
        {skillMoves && weakFoot && (
          <CardSkillMovesWeakFoot
            {...{ skillMoves, weakFoot, topText, strip }}
          />
        )}
      </div>
      <TopLeftArea
        {...{ rating, position, chemStyleId, firstOwner, topText }}
      />
      <BottomArea
        {...{
          bottomText,
          cardName,
          cardFont,
          position,
          attributes,
          isOnPitch,
          isOnBench,
          tabletOrLargerMode,
          assetsBaseUrl,
          countryId,
          leagueId,
          clubId,
          isLight,
        }}
      />
      {formationPosition && !isDragging && (
        <PositionOnPitch
          {...{ formationPosition, plus, plusPlus, tabletOrLargerMode }}
        />
      )}
      {chemistry && !isDragging && (
        <ChemistrySection {...{ chemistry, tabletOrLargerMode }} />
      )}
      {isLoanPlayer && <LoanPlayerIcon numberOfGames={numberOfLoanedGames} />}
      {!isDragging && (
        <BelowCard
          {...{
            metaRating,
            // Out of position on the pitch has no meaningful tier
            tier: chemistry?.isOutOfPosition && !isOnBench ? undefined : tier,
          }}
        />
      )}
      {(evolutionPath?.length > 0 || evoPathId || isEvoPlayer) && (
        <EvolutionsIconComponent
          outlineColor={topText}
          backgroundColor={strip}
          isOnPitch={isOnPitch}
          isOnBench={isOnBench}
        />
      )}
    </div>
  );
};
